import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@primer/react';
import storage from '@/utils/storage';
import { useRefreshAccessToken } from '@/features/auth/api/refreshAccessToken';
import { jwtDecode } from 'jwt-decode';

interface AccessTokenPayload {
  exp: number;
}
const MAX_RETRIES = 4;
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry
const PERIODIC_CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds

/** Returns the ms‐timestamp when the current access‐JWT actually expires. */
function getAccessTokenExpiryMs(): number | null {
  const raw = storage.getAccessToken();
  if (!raw) return null;
  try {
    const { exp } = jwtDecode<AccessTokenPayload>(raw);
    return exp * 1000;
  } catch {
    return null;
  }
}

/** Returns the ms‐timestamp when the current id‐token actually expires. */
function getIdTokenExpiryMs(): number | null {
  const raw = storage.getIdToken();
  if (!raw) return null;
  try {
    const { exp } = jwtDecode<AccessTokenPayload>(raw);
    return exp * 1000;
  } catch {
    return null;
  }
}

/** Check if either access token or id token needs refresh (5 minutes before expiry) */
function shouldRefreshTokens(): boolean {
  const accessExpMs = getAccessTokenExpiryMs();
  const idExpMs = getIdTokenExpiryMs();
  const now = Date.now();

  // If we have tokens, check if either expires within 5 minutes
  if (accessExpMs && accessExpMs - now <= REFRESH_THRESHOLD_MS) {
    return true;
  }

  if (idExpMs && idExpMs - now <= REFRESH_THRESHOLD_MS) {
    return true;
  }

  return false;
}

export const AccessTokenRefresh = () => {
  const { mutateAsync, isLoading } = useRefreshAccessToken();
  const timer = useRef<number | null>(null);
  const periodicCheckTimer = useRef<number | null>(null);
  const didInitialRefresh = useRef(false);
  const [waitingForToken, setWaitingForToken] = useState(false);
  const lastRefreshTime = useRef<number>(0);

  /**
   * Try refreshing, retrying up to `retriesLeft` times on error
   */
  async function doRefresh(retriesLeft = MAX_RETRIES) {
    // prevent concurrent calls and avoid too frequent refreshes
    if (isLoading || Date.now() - lastRefreshTime.current < 10000) return;

    const rt = storage.getRefreshToken();
    if (!rt) return scheduleNext();

    try {
      lastRefreshTime.current = Date.now();
      const result = await mutateAsync(rt);

      // Verify tokens were actually updated in storage
      const newAccessToken = storage.getAccessToken();
      const newIdToken = storage.getIdToken();

      console.log('Token refresh successful', {
        hasNewAccessToken: !!newAccessToken,
        hasNewIdToken: !!newIdToken,
        timestamp: new Date().toISOString(),
      });

      // Show spinner for 2 more seconds after token is set
      if (newAccessToken) {
        setTimeout(() => {
          setWaitingForToken(false);
        }, 2000);
      } else {
        setWaitingForToken(false);
      }

      // on success: schedule the next refresh based on new token
      scheduleNext();
    } catch (err) {
      if (retriesLeft > 0) {
        // retry with exponential backoff
        const delay = (MAX_RETRIES - retriesLeft + 1) * 2000;
        console.warn(`Refresh failed, retrying in ${delay}ms (${retriesLeft} left)`, err);
        setTimeout(() => doRefresh(retriesLeft - 1), delay);
      } else {
        console.error('Refresh failed after maximum retries', err);
        setWaitingForToken(false);
        // give up and schedule the next "normal" refresh
        scheduleNext();
      }
    }
  }

  /** Schedules the next attempt 5 minutes before expiry */
  function scheduleNext() {
    if (timer.current) clearTimeout(timer.current);

    const accessExpMs = getAccessTokenExpiryMs();
    const idExpMs = getIdTokenExpiryMs();
    const hasRefreshToken = !!storage.getRefreshToken();

    // If no access token but has refresh token → refresh immediately
    if (!accessExpMs && hasRefreshToken) {
      setWaitingForToken(true);
      doRefresh().then(() => {
        // Check if we now have a token after refresh
        const newAccessToken = storage.getAccessToken();
        if (newAccessToken) {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      });
      return;
    }

    // If no valid tokens and no refresh token → poll until one appears
    if (!accessExpMs && !idExpMs) {
      timer.current = window.setTimeout(scheduleNext, PERIODIC_CHECK_INTERVAL);
      return;
    }

    // Find the earliest expiry time
    const earliestExpiry = Math.min(...([accessExpMs, idExpMs].filter(Boolean) as number[]));

    const delay = earliestExpiry - REFRESH_THRESHOLD_MS - Date.now();

    if (delay <= 0) {
      // Token expires within 5 minutes or has already expired
      // console.log('Token refresh needed immediately', {
      //   accessExpiry: accessExpMs ? new Date(accessExpMs).toISOString() : null,
      //   idExpiry: idExpMs ? new Date(idExpMs).toISOString() : null,
      //   now: new Date().toISOString(),
      // });
      doRefresh();
    } else {
      // console.log('Token refresh scheduled', {
      //   delay: Math.round(delay / 1000),
      //   scheduledTime: new Date(Date.now() + delay).toISOString(),
      // });
      timer.current = window.setTimeout(() => doRefresh(), delay);
    }
  }

  /** Periodically check expiration and schedule refresh if needed */
  function startPeriodicCheck() {
    if (periodicCheckTimer.current) clearInterval(periodicCheckTimer.current);
    periodicCheckTimer.current = window.setInterval(() => {
      // const accessExpMs = getAccessTokenExpiryMs();
      // const idExpMs = getIdTokenExpiryMs();
      // const now = Date.now();

      // Log periodic check for debugging
      // console.log('Periodic token check', {
      //   accessTokenExpiry: accessExpMs ? new Date(accessExpMs).toISOString() : 'none',
      //   idTokenExpiry: idExpMs ? new Date(idExpMs).toISOString() : 'none',
      //   timeUntilAccessExpiry: accessExpMs ? Math.round((accessExpMs - now) / 1000) : null,
      //   timeUntilIdExpiry: idExpMs ? Math.round((idExpMs - now) / 1000) : null,
      //   refreshThresholdSeconds: REFRESH_THRESHOLD_MS / 1000,
      //   currentTime: new Date().toISOString(),
      // });

      if (shouldRefreshTokens()) {
        // console.log('Periodic check: tokens need refresh - initiating refresh');
        doRefresh();
      }
    }, PERIODIC_CHECK_INTERVAL);
  }

  useEffect(() => {
    // Check if we need to show spinner initially (no access token but has refresh token)
    const accessToken = storage.getAccessToken();
    const refreshToken = storage.getRefreshToken();

    if (!accessToken && refreshToken) {
      setWaitingForToken(true);
    }

    // detect a hard reload
    const nav = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isReload =
      nav.length > 0
        ? nav[0].type === 'reload'
        : // fallback for older browsers
          performance.navigation?.type === performance.navigation.TYPE_RELOAD;

    if (isReload && !didInitialRefresh.current) {
      didInitialRefresh.current = true;
      // console.log('Page reload detected, refreshing tokens');
      doRefresh();
    } else {
      // Check if tokens need immediate refresh
      if (shouldRefreshTokens()) {
        // console.log('Initial load: tokens need refresh');
        doRefresh();
      } else {
        scheduleNext();
      }
    }

    // Start periodic check
    startPeriodicCheck();

    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (periodicCheckTimer.current) clearInterval(periodicCheckTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (waitingForToken) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'white',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size="large" />
      </div>
    );
  }

  return null;
};
